import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { pricingStructuresApi, type PricingStructure } from '@/api/pricing-structures';

const MIN_LOADING_DURATION_MS = 500;

export interface TransformedPricingStructure {
  weekCost: number;
  weekendCost: number;
  weekCostPerHour: number;
  weekendCostPerHour: number;
  weekPrice: number;
  weekendPrice: number;
  weekPricePerHour: number;
  weekendPricePerHour: number;
  withTaxBill: boolean;
}

export const EMPTY_PRICING: TransformedPricingStructure = {
  weekCost: 0,
  weekendCost: 0,
  weekCostPerHour: 0,
  weekendCostPerHour: 0,
  weekPrice: 0,
  weekendPrice: 0,
  weekPricePerHour: 0,
  weekendPricePerHour: 0,
  withTaxBill: false,
};

export function transformPricingStructure(pricing?: PricingStructure): TransformedPricingStructure {
  if (!pricing) return EMPTY_PRICING;

  return {
    weekCost: pricing.caregiverWeekShiftCost,
    weekendCost: pricing.caregiverWeekendShiftCost,
    weekCostPerHour: pricing.caregiverWeekShiftCostPerHour,
    weekendCostPerHour: pricing.caregiverWeekendShiftCostPerHour,
    weekPrice: pricing.weekShiftPrice,
    weekendPrice: pricing.weekendShiftPrice,
    weekPricePerHour: pricing.weekShiftPricePerHour,
    weekendPricePerHour: pricing.weekendShiftPricePerHour,
    withTaxBill: pricing.withTaxBill,
  };
}

// Shared with usePricingStructures so both composables hit the same cache entry.
export function pricingStructureQueryKey(patientId: number | undefined, caregiverId: number | undefined) {
  return ['pricingStructure', patientId, caregiverId];
}

export function usePricingStructure(
  patientId: Ref<number | undefined>,
  caregiverId: Ref<number | undefined>,
  enabled: Ref<boolean> | boolean = true,
) {
  const { data, isLoading: queryIsLoading, refetch } = useQuery({
    queryKey: computed(() => pricingStructureQueryKey(patientId.value, caregiverId.value)),
    queryFn: () => {
      if (!patientId.value || !caregiverId.value) {
        return Promise.resolve(undefined);
      }

      return pricingStructuresApi.show(patientId.value, caregiverId.value);
    },
    enabled: computed(() => {
      const isEnabled = typeof enabled === 'boolean' ? enabled : enabled.value;
      return isEnabled && !!patientId.value && !!caregiverId.value;
    }),
  });

  const minLoadingTimeElapsed = ref(true);
  let loadingTimer: ReturnType<typeof setTimeout> | null = null;

  watch(queryIsLoading, (loading) => {
    if (loading) {
      if (loadingTimer) clearTimeout(loadingTimer);
      minLoadingTimeElapsed.value = false;
      loadingTimer = setTimeout(() => {
        minLoadingTimeElapsed.value = true;
      }, MIN_LOADING_DURATION_MS);
    }
  }, { immediate: true });

  const isLoading = computed(() => queryIsLoading.value || !minLoadingTimeElapsed.value);

  const pricingStructure: ComputedRef<TransformedPricingStructure> = computed(() =>
    transformPricingStructure(data.value),
  );

  return {
    pricingStructure,
    isLoading,
    refetch,
  };
}

export function useInitialPricingStructure(
  patientId: number | undefined,
  caregiverId: number | undefined,
): TransformedPricingStructure {
  const queryClient = useQueryClient();

  if (!patientId || !caregiverId) return EMPTY_PRICING;

  const cached = queryClient.getQueryData<PricingStructure>(
    pricingStructureQueryKey(patientId, caregiverId),
  );

  if (cached) {
    return transformPricingStructure(cached);
  }

  return EMPTY_PRICING;
}
